import { useState, useEffect, useMemo } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "config/firebase";
import { toast } from "react-toastify";
import {
  createAutomation,
  updateAutomation,
  deleteAutomation,
  executePrognosAutomationRemote,
  generateAutomationWithAIRemote,
} from "api/automations.api";

export const PROGNOS_TEMPLATES = [
  {
    id: "daily-armenia-news",
    name: "Հայկական լրահոսի օրական սկաներ",
    description:
      "Ամեն օր ավտոմատ որոնում է կանխատեսումներ և խոստումներ հայկական առաջատար լրատվամիջոցներում (news.am, armenpress.am, azatutyun.am, civilnet.am, 1lurer.am):",
    targetEntities: ["predictions", "promises"],
    trigger: {
      type: "schedule",
      schedule: {
        interval: "day",
      },
    },
    searchConfig: {
      query: "կանխատեսում OR խոստանում եմ OR հայտարարել է OR սպասվում է",
      topic: "general",
      maxResults: 15,
      searchDepth: "advanced",
      includeDomains: ["news.am", "armenpress.am", "azatutyun.am", "civilnet.am", "1lurer.am", "aravot.am", "pastv.am", "tert.am", "168.am"],
    },
    autoDraft: false,
    status: "published",
  },
  {
    id: "gov-promises-tracker",
    name: "Պաշտոնյաների և Կառավարության խոստումների մոնիտորինգ",
    description:
      "Մոնիտորինգ է անում ՀՀ Վարչապետի, նախարարների և Երևանի քաղաքապետարանի հանրային խոստումներն ու ստանձնած պարտավորությունները:",
    targetEntities: ["promises"],
    trigger: {
      type: "schedule",
      schedule: {
        interval: "12hours",
      },
    },
    searchConfig: {
      query:
        "խոստանում ենք OR պարտավորվում ենք OR ավարտին կհասցնենք OR կկառուցվի OR կվերանորոգվի (Փաշինյան OR Ավինյան OR նախարարություն)",
      topic: "general",
      maxResults: 15,
      searchDepth: "advanced",
      includeDomains: ["news.am", "armenpress.am", "1lurer.am", "primeminister.am", "aravot.am", "tert.am"],
    },
    autoDraft: false,
    status: "published",
  },
  {
    id: "economic-forecasts",
    name: "Տնտեսական, գնաճի և փոխարժեքի կանխատեսումներ",
    description:
      "Որոնում է Կենտրոնական բանկի, տնտեսագետների և ֆինանսական վերլուծաբանների կանխատեսումները ՀՀ դրամի, գնաճի և ՀՆԱ-ի վերաբերյալ:",
    targetEntities: ["predictions"],
    trigger: {
      type: "schedule",
      schedule: {
        interval: "day",
      },
    },
    searchConfig: {
      query:
        "տնտեսական աճ OR գնաճ OR դոլարի փոխարժեք OR ՀՆԱ կանխատեսում (Կենտրոնական բանկ OR տնտեսագետ)",
      topic: "general",
      maxResults: 15,
      searchDepth: "advanced",
      includeDomains: ["banks.am", "b24.am", "arka.am", "news.am", "armenpress.am"],
    },
    autoDraft: false,
    status: "published",
  },
  {
    id: "trending-surveys",
    name: "Հանրային բանավեճերից Հարցումների (Surveys) գեներատոր",
    description:
      "Գտնում է հանրային ամենաթեժ և հակասական քննարկումները և առաջարկում նոր հարցումներ Prognos.am-ի համայնքի համար:",
    targetEntities: ["surveys"],
    trigger: {
      type: "schedule",
      schedule: {
        interval: "day",
      },
    },
    searchConfig: {
      query:
        "հանրային քննարկում OR բողոքի ակցիա OR նոր օրենք OR սակագնի փոփոխություն OR հասարակական կարծիք",
      topic: "general",
      maxResults: 15,
      searchDepth: "advanced",
      includeDomains: ["news.am", "civilnet.am", "azatutyun.am", "factor.am", "1lurer.am", "aravot.am"],
    },
    autoDraft: false,
    status: "published",
  },
];

const useAutomations = () => {
  const [automations, setAutomations] = useState([]);
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(null); // stores automationId being executed
  const [isGenerating, setIsGenerating] = useState(false);

  // 1. Sync Automations
  useEffect(() => {
    const q = query(
      collection(db, "automations"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetched = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setAutomations(fetched);
        setIsLoading(false);
      },
      (error) => {
        console.error("Firestore automations listener error:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // 2. Sync Execution Runs
  useEffect(() => {
    const q = query(
      collection(db, "automation_runs"),
      orderBy("startedAt", "desc"),
      limit(30)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetched = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setRuns(fetched);
      },
      (error) => {
        console.error("Firestore runs listener error:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Stats calculation
  const stats = useMemo(() => {
    const totalAutomations = automations.length;
    const activeAutomations = automations.filter(
      (a) => a.status === "published"
    ).length;
    const totalRuns = runs.length;
    const successfulRuns = runs.filter((r) => r.status === "success").length;
    const totalFindings = runs.reduce(
      (acc, r) => acc + (r.findingsCount || 0),
      0
    );

    return {
      totalAutomations,
      activeAutomations,
      totalRuns,
      successRate:
        totalRuns > 0
          ? `${Math.round((successfulRuns / totalRuns) * 100)}%`
          : "100%",
      totalFindings,
    };
  }, [automations, runs]);

  // Actions
  const triggerRun = async (automationId, inlineConfig = null) => {
    setIsExecuting(automationId || "inline");
    try {
      toast.info("Գործարկվում է որոնումը և AI վերլուծությունը...");
      const result = await executePrognosAutomationRemote(
        automationId,
        inlineConfig
      );
      if (result?.success) {
        toast.success(
          `Հաջողությամբ գտնվել է ${result.findingsCount || 0} նոր նյութ!`
        );
        return result;
      } else {
        toast.error("Գործընթացը չհաջողվեց:");
      }
    } catch (err) {
      console.error("Run error:", err);
      toast.error(`Սխալ գործարկման ժամանակ՝ ${err.message}`);
    } finally {
      setIsExecuting(null);
    }
    return null;
  };

  const toggleStatus = async (automation) => {
    const newStatus =
      automation.status === "published" ? "paused" : "published";
    try {
      await updateAutomation(automation.id, { status: newStatus });
      toast.success(
        newStatus === "published" ? "Ավտոմատացումն ակտիվացված է:" : "Կասեցված է:"
      );
    } catch (err) {
      toast.error("Կարգավիճակի փոփոխումը ձախողվեց:");
    }
  };

  const removeAutomation = async (id) => {
    try {
      await deleteAutomation(id);
      toast.success("Ավտոմատացումը հեռացված է:");
    } catch (err) {
      toast.error("Հեռացումը ձախողվեց:");
    }
  };

  const saveAutomation = async (data, id = null) => {
    try {
      if (id) {
        await updateAutomation(id, data);
        toast.success("Փոփոխությունները պահպանված են:");
        return id;
      } else {
        const newId = await createAutomation(data);
        toast.success("Նոր ավտոմատացումը ստեղծված է:");
        return newId;
      }
    } catch (err) {
      toast.error("Պահպանումը ձախողվեց:");
      throw err;
    }
  };

  const buildWithAI = async (prompt) => {
    if (!prompt?.trim()) {
      toast.warn("Խնդրում ենք նկարագրել առաջադրանքը:");
      return null;
    }
    setIsGenerating(true);
    try {
      toast.info("Gemini AI-ն կառուցում է ավտոմատացման կանոնները...");
      const res = await generateAutomationWithAIRemote(prompt);
      if (res?.success && res?.data) {
        const newId = await createAutomation(res.data);
        toast.success(`AI-ն ստեղծեց «${res.data.name}» ավտոմատացումը:`);
        return { id: newId, ...res.data };
      }
    } catch (err) {
      toast.error(`AI գեներացիայի սխալ՝ ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
    return null;
  };

  const createFromTemplate = async (template) => {
    try {
      const { id: _, ...templateData } = template;
      const newId = await createAutomation(templateData);
      toast.success(`«${template.name}» շաբլոնը հաջողությամբ ավելացված է:`);
      return newId;
    } catch (err) {
      toast.error("Շաբլոնի ավելացումը ձախողվեց:");
      throw err;
    }
  };

  return {
    automations,
    runs,
    isLoading,
    isExecuting,
    isGenerating,
    stats,
    triggerRun,
    toggleStatus,
    removeAutomation,
    saveAutomation,
    buildWithAI,
    createFromTemplate,
  };
};

export default useAutomations;
